import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { HeaderTheme, HeaderSize, HeaderThemeType, HeaderSizeType } from '../constants/header';
import { LocalNavVerticalItem, VerticalItem } from '../constants/lnb';
import { readRegionCookie, writeRegionCookie } from '../app/utils/regionCookie';
import { fetchGnb } from '../data/discovery';
import { regionFromQueryParam, resolveRegion } from '../data/resolveRegion';
import { parseShortcutToVerticalItems } from '../data/parseShortcut';
import { HeaderRegion, HeaderVerticalItems } from '../data/types';
import DomainManager from '../shared/utils/DomainManager';
import { useEnvContext } from './EnvProvider';

export type LayoutHeaderContextType = {
  isFlightMode: boolean;
  isDisabled: boolean;
  theme: HeaderThemeType;
  size: HeaderSizeType;
  user: {
    id: number;
    hashMemberId: string;
    username: string;
    email: string;
    phoneNumber: string;
    point: number;
    numberOfCoupons: number;
    profileImage: string;
  } | null;
  lnbVerticalItems: LocalNavVerticalItem[];
  mainVerticalItems: VerticalItem[] | null;
  subVerticalItems: VerticalItem[] | null;
  isMainPage?: boolean;
  excludeLnb?: boolean;
  pathname?: string;
  // PST-570 — 해외/국내 탭. 옵셔널(additive) — 미주입 앱은 토글 미노출(기존 동작 불변).
  // onRegionChange 는 소비처가 주입(URL 네비/로깅). 주입된 경우에만 데스크톱 GNB 에 토글 노출.
  region?: HeaderRegion;
  // PST-570 — 주입/URL/쿠키로 최종 확정된 region. 프로바이더(GNB·LNB 공통 부모)가 1회 계산해 제공한다.
  // GNB 는 이 값을 단일 소스로 읽어 토글 표시와 네비 데이터가 어긋나지 않게 한다.
  resolvedRegion?: HeaderRegion;
  // PST-570 — region 매칭 GNB self-fetch 진행 중 여부(= !gnbSettled). true 면 LNB 는 스켈레톤 렌더.
  gnbLoading?: boolean;
  // PST-570 — region(주입/URL/쿠키) 확정 여부. false 인 동안(첫 렌더~mount effect 전)엔 토글이
  // 선택 표시를 유예해 default('overseas')→확정 region 의 chip flip(덜컥)을 막는다.
  regionResolved?: boolean;
  // member(user) 조회 진행 중 여부. true 인 동안엔 GNB/SideBar 가 로그인/로그아웃 UI 판정을 유예해
  // (아직 미확정인 user.id=0 을 '로그아웃'으로 오인한) 로그인 버튼 flash 를 막는다.
  // 미주입(기본 undefined) 시 즉시 판정 → SSR 로 member 를 hydrate 하는 앱은 기존 동작 불변.
  isMemberLoading?: boolean;
  onRegionChange?: (region: HeaderRegion) => void;
  // 해외탭일 때 국내 chip 아래 안내 말풍선 텍스트(예: "최대 5만원 쿠폰이 있어요!"). 없으면 미노출.
  regionToggleBadge?: string;
  setTheme: (nextTheme: HeaderThemeType) => void;
  setSize: (nextSize: HeaderSizeType) => void;
  setDisabled: (nextDisabled: boolean) => void;
  setExcludeLnb: (nextExcludeLnb: boolean) => void;
};

const defaultState = {
  user: null,
  isDisabled: false,
  lnbVerticalItems: [],
  mainVerticalItems: [],
  subVerticalItems: [],
  theme: HeaderTheme.white,
  size: HeaderSize.default,
  isFlightMode: false,
  excludeLnb: false,
  isMainPage: false,
  // region 은 기본값을 두지 않는다 — 미주입 시 undefined 여야 아래 resolver 가 URL ?isDomestic= / 쿠키를
  // 폴백으로 소비한다(기본 'overseas' 를 넣으면 injectedRegion 이 항상 truthy → URL·쿠키가 무시됨).
  setTheme: () => null,
  setSize: () => null,
  setDisabled: () => null,
  setExcludeLnb: () => null,
};

export const LayoutHeaderContext = createContext<LayoutHeaderContextType>(defaultState);

export function LayoutHeaderContextProvider({
  initialState,
  children,
}: PropsWithChildren<{
  initialState: Partial<LayoutHeaderContextType>;
}>) {
  const combineState = { ...defaultState, ...initialState };
  const [currentTheme, setCurrentTheme] = useState<HeaderThemeType>(combineState.theme);
  const [currentSize, setCurrentSize] = useState<HeaderSizeType>(combineState.size);
  const [isExcludeLnb, setExcludeLnb] = useState<boolean>(combineState.excludeLnb ?? false);
  const [currentDisabled, setCurrentDisabled] = useState<boolean>(combineState.isDisabled ?? false);

  const handleTheme = useCallback((nextTheme: HeaderThemeType) => setCurrentTheme(nextTheme), []);
  const handleSize = useCallback((nextSize: HeaderSizeType) => setCurrentSize(nextSize), []);
  const handleDisabled = useCallback(
    (nextDisabled: boolean) => setCurrentDisabled(nextDisabled),
    [],
  );
  const handleExcludeLnb = useCallback(
    (nextExcludeLnb: boolean) => setExcludeLnb(nextExcludeLnb),
    [],
  );

  // PST-570 — region→GNB 단일 소스화. GNB·LNB 공통 부모인 이 프로바이더에서 region 을 1회 확정하고,
  // 그 region 에 맞는 GNB 를 self-fetch 해 main/sub/lnb VerticalItems 를 덮어써 토글과 네비 행을 일치시킨다.
  // 우선순위: 주입 region(props) ?? URL ?isDomestic= ?? 쿠키(mrt_region) ?? overseas.
  // SSR·최초 client 렌더는 주입값(없으면 overseas)로 일치시키고, mount 후 effect 에서 URL/쿠키를 반영한다
  // (hydration mismatch 회피 — GNB 기존 방식과 동일).
  const injectedRegion = combineState.region;
  const [fallbackRegion, setFallbackRegion] = useState<HeaderRegion | null>(null);
  useEffect(() => {
    // URL·쿠키는 client 전용 → mount 후 반영(hydration mismatch 회피).
    const urlRegion = regionFromQueryParam(
      new URLSearchParams(window.location.search).get('isDomestic'),
    );
    // 권위 신호(주입 ?? URL)만 연속성 쿠키에 기록 — write 는 순수 resolver 밖에 유지.
    const authoritative = injectedRegion ?? urlRegion;
    if (authoritative) writeRegionCookie(authoritative);
    // fallback = 주입 ?? URL ?? 쿠키 ?? overseas (공통 resolver).
    setFallbackRegion(resolveRegion([injectedRegion, urlRegion, readRegionCookie() ?? undefined]));
  }, [injectedRegion]);
  // PST-570 — 항상 'domestic'|'overseas' (never undefined) → fetchGnb 가 항상 ?isDomestic= 전송 → 통합 WEB_GNB 미조회.
  const resolvedRegion: HeaderRegion = injectedRegion ?? fallbackRegion ?? 'overseas';
  // PST-570 — region 미확정(주입 X + fallback 미결정) 동안 GNB self-fetch 를 보류한다.
  //   주입 없는 앱은 첫 렌더 폴백('overseas')으로 해외 GNB 를 선요청하는데, mount 후 URL/쿠키로 region 이
  //   확정되며 재요청되어 이중 fetch(+불필요한 해외 GNB 요청)가 발생한다. fallback 확정 후 1회만 요청하도록 게이팅.
  //   (주입 앱은 injectedRegion 이 defined 라 항상 true → 기존과 동일하게 즉시 1회 요청.)
  const isRegionResolved = injectedRegion !== undefined || fallbackRegion !== null;

  // PST-570 — resolvedRegion 에 맞는 GNB self-fetch(react-query 미사용 plain fetch → SSR 안전, 패키지 경계 무관).
  // baseUrl 은 targetEnv(EnvProvider)에서 파생: https://api3.{env-}myrealtrip.com + /discovery/api/v1/web/gnb.
  // 성공 시에만 override, 실패/null 은 앱 주입 items(combineState.*)로 폴백 → 네비가 절대 비지 않는다.
  // gnbSettled: fetch 가 resolve(성공/실패 무관)되면 true → 그 전까지 LNB 는 스켈레톤(default→region flash 방지).
  const { targetEnv } = useEnvContext();
  const baseUrl = useMemo(() => new DomainManager(targetEnv).getApiDomain(), [targetEnv]);
  const [fetchedItems, setFetchedItems] = useState<HeaderVerticalItems | null>(null);
  const [gnbSettled, setGnbSettled] = useState(false);
  useEffect(() => {
    // isDisabled 헤더·baseUrl 없음: self-fetch 스킵. 스켈레톤 없이 앱 주입 items 즉시 렌더(settled=true).
    if (combineState.isDisabled || !baseUrl) {
      setGnbSettled(true);
      return;
    }
    // region 확정 전에는 fetch 보류(스켈레톤 유지) → 미확정 'overseas' 선요청/이중 fetch 방지.
    if (!isRegionResolved) return;
    let alive = true;
    setGnbSettled(false);
    void fetchGnb({ baseUrl, region: resolvedRegion }).then((response) => {
      if (!alive) return;
      setFetchedItems(response ? parseShortcutToVerticalItems(response) : null);
      setGnbSettled(true);
    });
    return () => {
      alive = false;
    };
  }, [baseUrl, resolvedRegion, isRegionResolved, combineState.isDisabled]);

  return (
    <LayoutHeaderContext.Provider
      value={{
        ...combineState,
        theme: currentTheme,
        size: currentSize,
        isDisabled: currentDisabled,
        excludeLnb: isExcludeLnb,
        // PST-570 — region 확정값 + region 매칭 GNB override(성공 시). 미수신/실패 시 앱 주입 items 폴백.
        // gnbLoading=true 인 동안 LNB 는 스켈레톤을 렌더한다(default→region flash 방지).
        resolvedRegion,
        gnbLoading: !gnbSettled,
        regionResolved: isRegionResolved,
        mainVerticalItems: fetchedItems?.vertical_items ?? combineState.mainVerticalItems,
        subVerticalItems: fetchedItems?.sub_vertical_items ?? combineState.subVerticalItems,
        lnbVerticalItems: fetchedItems?.lnb_vertical_items ?? combineState.lnbVerticalItems,
        setTheme: handleTheme,
        setSize: handleSize,
        setDisabled: handleDisabled,
        setExcludeLnb: handleExcludeLnb,
      }}
    >
      {children}
    </LayoutHeaderContext.Provider>
  );
}

export function useLayoutHeaderContext() {
  return useContext(LayoutHeaderContext);
}
